import { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import checkImage from "../images/check.png";
import coverImage from "../images/main-bg.jpg";
import { device } from "../variables";

const STATUS_THEMES = {
  ok: {
    card: "linear-gradient(180deg, rgba(94, 232, 160, 0.35) 0%, rgba(61, 214, 138, 0.35) 100%)",
    border: "#3dd68a",
    iconBg: "transparent",
    iconColor: "#0a6a41",
    text: "#000000",
    helper: "#0f2544",
  },
  loading: {
    card: "linear-gradient(180deg, rgba(126, 200, 247, 0.35) 0%, rgba(90, 180, 240, 0.35) 100%)",
    border: "#5ab4f0",
    iconBg: "linear-gradient(145deg, #ffffff 0%, #ddeaff 100%)",
    iconColor: "#185994",
    text: "#134172",
    helper: "#152f53",
  },
  error: {
    card: "linear-gradient(180deg, rgba(255, 179, 71, 0.35) 0%, rgba(255, 150, 51, 0.35) 100%)",
    border: "#ff9633",
    iconBg: "transparent",
    iconColor: "#ff8c00",
    text: "#000000",
    helper: "#2e1c18",
  },
};

const getTheme = (status) => STATUS_THEMES[status] || STATUS_THEMES.ok;

const Status = () => {
  const [statusMessage, setStatusMessage] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const logoSrc = `/misc/logo-textonly-usercentrics.png`;

  useEffect(() => {
    // Set theme-color meta tag for Safari browser UI
    const themeColor = "#4c9ceb";
    const existingMeta = document.querySelector('meta[name="theme-color"]');
    const originalColor = existingMeta?.getAttribute("content");

    if (existingMeta) {
      existingMeta.setAttribute("content", themeColor);
    } else {
      const meta = document.createElement("meta");
      meta.name = "theme-color";
      meta.content = themeColor;
      document.head.appendChild(meta);
    }

    return () => {
      // Restore original theme-color on unmount
      if (existingMeta && originalColor) {
        existingMeta.setAttribute("content", originalColor);
      }
    };
  }, []);

  useEffect(() => {
    fetch("/status-message.txt")
      .then((response) => {
        if (response.ok) {
          return response.text();
        }
        return "";
      })
      .then((text) => {
        setStatusMessage(text.trim());
        setIsLoading(false);
      })
      .catch(() => {
        setStatusMessage("");
        setIsLoading(false);
      });
  }, []);

  const hasError = Boolean(statusMessage);
  const statusState = isLoading ? "loading" : hasError ? "error" : "ok";

  const headline =
    statusState === "loading"
      ? "Checking status..."
      : statusState === "error"
        ? "Issues detected."
        : "No issues detected.";

  const description =
    statusState === "error"
      ? statusMessage || "We're investigating the reported issues."
      : statusState === "loading"
        ? "Fetching the latest status..."
        : "All game servers are functioning normally.";

  return (
    <StatusWrapper>
      <BackgroundImage src={coverImage} alt="Chess Universe sky" />
      <ContentOverlay>
        <LogoContainer>
          <LogoImage src={logoSrc} alt="Chess Universe" />
        </LogoContainer>

        <StatusBox $status={statusState}>
          <StatusHeader>
            <StatusIconWrapper $status={statusState}>
              {statusState === "error" && (
                <IconSvg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  <path
                    d="M12 2L1 21h22L12 2z"
                    fill="currentColor"
                    stroke="currentColor"
                    strokeWidth="1"
                    strokeLinejoin="round"
                  />
                  <line
                    x1="12"
                    y1="9"
                    x2="12"
                    y2="14"
                    stroke="white"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                  />
                  <circle cx="12" cy="17.5" r="1.5" fill="white" stroke="none" />
                </IconSvg>
              )}
              {statusState === "ok" && <StatusIconImage src={checkImage} alt="Operational" />}
              {statusState === "loading" && <LoadingDot />}
            </StatusIconWrapper>
            <StatusTitle>{headline}</StatusTitle>
          </StatusHeader>
        </StatusBox>

        <StatusDescription $status={statusState}>{description}</StatusDescription>
      </ContentOverlay>

      <Footer>
        <FooterDivider />
        <FooterLegal>
          © Kings of Games. All rights reserved.
        </FooterLegal>
        <FooterLinks>
          <FooterLink href="/privacy">PRIVACY</FooterLink>
          <FooterSeparator>|</FooterSeparator>
          <FooterLink href="/terms">TERMS</FooterLink>
        </FooterLinks>
      </Footer>
    </StatusWrapper>
  );
};

export default Status;

const StatusWrapper = styled.div`
  position: relative;
  width: 100%;
  min-height: 100vh;
  overflow: hidden;
  font-family: "Poppins", "Trebuchet MS", sans-serif;
  background: linear-gradient(180deg, #4c9ceb 0%, #8fcdf7 40%, #ffe399 100%);
  color: #1f2c3a;
`;

const BackgroundImage = styled.img`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: auto;
  height: 100%;
  min-width: 100%;
  object-fit: cover;
  object-position: center center;
  opacity: 1;
  z-index: 1;
  pointer-events: none;
`;

const ContentOverlay = styled.div`
  position: relative;
  z-index: 2;
  min-height: 100vh;
  padding: clamp(22px, 5.5vw, 90px) clamp(20px, 7vw, 64px) clamp(120px, 14vh, 180px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  text-align: center;
  gap: clamp(24px, 4vh, 48px);
`;

const LogoContainer = styled.div`
  filter: drop-shadow(0 12px 22px rgba(25, 73, 117, 0.28));
  margin-top: clamp(4px, 2vh, 18px);
  margin-bottom: clamp(10px, 2.6vh, 24px);
`;

const LogoImage = styled.img`
  width: clamp(121px, 15.4vw, 176px);
  height: auto;
`;

const StatusBox = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: clamp(18px, 3vw, 30px);
  padding: clamp(20px, 3.5vw, 32px) clamp(48px, 10vw, 120px);
  width: min(720px, 90vw);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  background: ${({ $status }) => getTheme($status).card};
  color: ${({ $status }) => getTheme($status).text};
  border: 3px solid ${({ $status }) => getTheme($status).border};
`;

const StatusHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: clamp(14px, 2.5vw, 20px);
  width: 100%;
`;

const StatusIconWrapper = styled.div`
  position: relative;
  width: ${({ $status }) => ($status === "loading" ? "clamp(52px, 6vw, 68px)" : "clamp(42px, 5vw, 56px)")};
  height: ${({ $status }) => ($status === "loading" ? "clamp(52px, 6vw, 68px)" : "clamp(42px, 5vw, 56px)")};
  border-radius: ${({ $status }) => ($status === "loading" ? "50%" : "0")};
  background: ${({ $status }) => ($status === "loading" ? getTheme($status).iconBg : "transparent")};
  color: ${({ $status }) => getTheme($status).iconColor};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: ${({ $status }) =>
    $status === "loading"
      ? "0 12px 24px rgba(0, 0, 0, 0.15)"
      : "none"};
`;

const StatusIconImage = styled.img`
  width: clamp(42px, 5vw, 56px);
  height: clamp(42px, 5vw, 56px);
  object-fit: contain;
`;

const IconSvg = styled.svg`
  width: clamp(42px, 5vw, 56px);
  height: clamp(42px, 5vw, 56px);
`;

const pulse = keyframes`
  0% {
    transform: scale(0.8);
    opacity: 0.6;
  }
  50% {
    transform: scale(1);
    opacity: 1;
  }
  100% {
    transform: scale(0.8);
    opacity: 0.6;
  }
`;

const LoadingDot = styled.div`
  width: clamp(16px, 2.2vw, 22px);
  height: clamp(16px, 2.2vw, 22px);
  border-radius: 50%;
  background: currentColor;
  animation: ${pulse} 1.1s ease-in-out infinite;
`;

const StatusTitle = styled.h2`
  position: relative;
  z-index: 2;
  margin: 0;
  font-size: clamp(24px, 4vw, 36px);
  font-weight: 700;
  letter-spacing: 0.01em;
  color: inherit;
  text-align: left;
`;

const StatusDescription = styled.p`
  margin: clamp(16px, 3vh, 28px) auto 0;
  width: min(520px, 85vw);
  color: ${({ $status }) => getTheme($status).helper};
  font-size: clamp(24px, 3.3vw, 30px);
  font-weight: 400;
  letter-spacing: -0.03em;
  text-align: center;
  line-height: 1.6;
`;

const Footer = styled.footer`
  position: absolute;
  left: 0;
  right: 0;
  bottom: clamp(28px, 4vh, 48px);
  z-index: 3;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(31, 44, 58, 0.88);

  @media ${device.tablet} {
    font-size: 13px;
  }
`;

const FooterDivider = styled.span`
  width: min(620px, 78vw);
  height: 1px;
  background: rgba(31, 44, 58, 0.32);
`;

const FooterLegal = styled.div`
  padding-top: 4px;
  font-weight: 700;
`;

const FooterLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const FooterSeparator = styled.span`
  color: rgba(31, 44, 58, 0.6);
`;

const FooterLink = styled.a`
  color: inherit;
  text-decoration: none;
  font-weight: 700;

  &:hover {
    color: #0e4f8e;
    text-decoration: underline;
  }
`;
